import { BasicShape } from "../BasicShape";
import { findLinesIntersection, getSelectBoundaryRectangle, isin, isinSelectBoundaryLine, isPointInsideFrame } from "../../shared/common";
import { Point } from "../Point";
import { Vector } from "../Vector";

import { getDisplayedShapes } from "../../shared/globalState/a";
import { arrayFirst, arrayLast } from "../../utils/exstensions";
import { s } from "../../shared/globalState/settings";
import { Line } from "./Line";
import { Rectangle } from "./Rectangle";
import { Circle } from "./Circle";
import { NetworkInfo } from "../NetworkInfo";
import { ObjectTypes } from "../../shared/enums";
import { BlockData } from "../BlockData";
import { parseAirDuctSize } from "../../services/calculations";

import { Grip } from "../snaps/Grip";
import { TripH } from "../snaps/TripH";
import { TripV } from "../snaps/TripV";
import { RouteCrucialPoints, FlowDirection } from "../../shared/types";
import { ConnectionPoint } from "../nets/ConnectionPoint";
import { AbstractFrame } from "../frames/AbstractFrame";
import { Join } from "../Join";

interface LineMagnet {
    line: Line;
    gripStart: boolean;
    gripEnd: boolean;
    gripMid: boolean;
    tripHStart: boolean;
    tripHMid: boolean;
    tripHEnd: boolean;
    tripVStart: boolean;
    tripVMid: boolean;
    tripVEnd: boolean;
}

interface RectangleMagnet {
    rect: Rectangle;
    gripP1: boolean;
    gripP2: boolean;
    gripP3: boolean;
    gripP4: boolean;
    gripM1: boolean;
    gripM2: boolean;
    gripM3: boolean;
    gripM4: boolean;
    gripCenter: boolean;
    tripHtop: boolean;
    tripHbottom: boolean;
    tripVleft: boolean;
    tripVright: boolean;
    tripVMtop: boolean;
    tripVMbottom: boolean;
    tripHMright: boolean;
    tripHMleft: boolean;
}

interface CircleMagnet {
    circle: Circle;
    gripCenter: boolean;
    gripQ1: boolean;
    gripQ2: boolean;
    gripQ3: boolean;
    gripQ4: boolean;
    tripHCenter: boolean;
    tripVCenter: boolean;
    tripHq1: boolean;
    tripHq3: boolean;
    tripVq4: boolean;
    tripVq2: boolean;
}

export class Block extends BasicShape {

    type: string;
    shapes: (Line | Rectangle | Circle)[];
    color: number[];
    thickness: number;
    data: BlockData;
    net: NetworkInfo;
    cpoints: ConnectionPoint[];
    routeId: string | null = null;
    netId: string | null = null;
    isSelected: boolean = false;
    saved: Float32Array | null = null;
    zlc: number = 1;
    
    // Flow direction properties - known from construction
    /**
     * Направление потока для каждой точки подключения блока.
     * Значения устанавливаются в конструкторе, могут перезаписываться при восстановлении из базы
     * и обновляться в getCrucialPoints() (для mid) или updateFlowProperties().
     */
    flowDirections: {
        start: FlowDirection,
        end: FlowDirection,
        mid?: FlowDirection
    };
    /**
     * Флаг терминальности для каждой точки подключения блока.
     * Инициализируется при создании, переопределяется при восстановлении из базы и
     * корректируется в getCrucialPoints()/updateFlowProperties().
     */
    isTerminal: {
        start: boolean,
        end: boolean,
        mid?: boolean
    };
    /**
     * Давление (или аналогичный параметр) для каждой точки подключения блока.
     * Наполняется в момент создания, далее может быть восстановлено из базы и изменено через updateFlowProperties().
     */
    pressure: {
        start: number,
        end: number,
        mid?: number
    };

    constructor(shapes: (Line | Rectangle | Circle)[], color: Array<number>, thickness: number) {
        super();

        this.type = ObjectTypes.block;
        this.shapes = [...shapes];
        this.color = [...color];
        this.thickness = thickness;
        this.data = new BlockData();

        this.net = new NetworkInfo(ObjectTypes.block, null, null, null, null, null, null);


        /**
         * как определить, соединена ли линия с точкой?
         * можно определить операции, в которых это будет проверяться, например операция move
         */
        this.cpoints = []; // connection points

        this.shapes.forEach(s => {
            s.thickness = this.thickness;
            s.color = this.color;
        });

        this.routeId = null;
        this.isSelected = false;

        // Initialize flow direction properties - known from construction
        // Значения по умолчанию для направлений потока
        this.flowDirections = {
            start: FlowDirection.UNKNOWN,
            end: FlowDirection.UNKNOWN,
            mid: undefined // Will be set when connection points are assigned
        };
        
        // Значения по умолчанию для признака терминальности
        this.isTerminal = {
            start: false,
            end: false,
            mid: undefined // Will be set when connection points are assigned
        };
        
        // Значения по умолчанию для давления
        this.pressure = {
            start: 0,
            end: 0,
            mid: undefined // Will be set when connection points are assigned
        };

    }

    getVertices(): Float32Array | null {
        var arr: Float32Array[] = [];
        this.shapes.forEach(shape => {
            const vertices = shape.getVertices();
            if (vertices instanceof Float32Array) {
                arr.push(vertices);
            }
        });

        return concat(arr);
    }
    getVerticesArray(): Float32Array[] {
        var arr: Float32Array[] = [];
        this.shapes.forEach(shape => {
            const vertices = shape.getVertices();
            if (vertices instanceof Float32Array) {
                arr.push(vertices);
            }
        });
        return arr;
    }

    getClone() {
        var lines = this.shapes.filter(s => s.type === ObjectTypes.line).map(s => s.getClone());
        var rects = this.shapes.filter(s => s.type === ObjectTypes.rectangle).map(s => s.getClone());
        var circle = this.shapes.filter(s => s.type === ObjectTypes.circle).map(s => s.getClone());

        var shapes = lines.concat(rects).concat(circle);

        var block = new Block(shapes, this.color, this.thickness);
        block.routeId = this.routeId;

        var cpoints = this.cpoints.map(c => c.clone());
        block.cpoints = cpoints;

        if (this.net) {
            block.net = this.net.getClone();
        }

        return block;
    }


    assignConnectionPoint(point: Point): ConnectionPoint {
        var cPoint = new ConnectionPoint(point.x, point.y);
        this.cpoints.push(cPoint);
        return cPoint;
    }
    updateSavedVertices(): void {
        this.saved = this.getVertices();
    }

    getLeftTop() {

        if (!this.shapes.length) return null;

        var { xes, yes } = getShapesPoints(this.shapes);

        var minX = Math.min(...xes);
        var minY = Math.min(...yes);

        return new Point(minX, minY);

    }

    getRightTop() {

        if (!this.shapes.length) return null;

        var { xes, yes } = getShapesPoints(this.shapes);

        var maxX = Math.max(...xes);
        var minY = Math.min(...yes);

        return new Point(maxX, minY);
    }

    getRightBottom() {

        if (!this.shapes.length) return null;

        var { xes, yes } = getShapesPoints(this.shapes);

        var maxX = Math.max(...xes);
        var maxY = Math.max(...yes);

        return new Point(maxX, maxY);
    }

    getLeftBottom() {

        if (!this.shapes.length) return null;

        var { xes, yes } = getShapesPoints(this.shapes);

        var minX = Math.min(...xes);
        var maxY = Math.max(...yes);

        return new Point(minX, maxY);
    }

    isinSelectBoundary(mouse: Point): boolean {
        var p1 = this.getLeftTop();
        var p2 = this.getRightTop();
        var p3 = this.getRightBottom();
        var p4 = this.getLeftBottom();
        
        if (!p1 || !p2 || !p3 || !p4) return false;

        const isinTop = isinSelectBoundaryLine(mouse, p1, p2);
        const isinRight = isinSelectBoundaryLine(mouse, p2, p3);
        const isinBottom = isinSelectBoundaryLine(mouse, p4, p3);
        const isinLeft = isinSelectBoundaryLine(mouse, p1, p4);

        if (isinTop || isinBottom || isinRight || isinLeft) {
            return true;
        }
        return false;
    }

    setSelectBoundary(): void {
        var p1 = this.getLeftTop();
        var p2 = this.getRightTop();
        var p3 = this.getRightBottom();
        var p4 = this.getLeftBottom();
        
        if (!p1 || !p2 || !p3 || !p4) return;

        var shapesPoints = this.shapes
            .filter((s): s is Line => s.type === ObjectTypes.line)
            .map((s: Line) => {
                return [s.start, s.end];
            });
        
        this.selectBoundary = getSelectBoundaryRectangle(p1, p2, p3, p4);
    }

    isinSelectFrameAtLeast(frame: AbstractFrame): boolean {
        const p1 = this.getLeftTop();
        const p2 = this.getRightTop();
        const p3 = this.getRightBottom();
        const p4 = this.getLeftBottom();
        
        if (!p1 || !p2 || !p3 || !p4) return false;

        if (
            isPointInsideFrame(frame, p1.x, p1.y) ||
            isPointInsideFrame(frame, p2.x, p2.y) ||
            isPointInsideFrame(frame, p3.x, p3.y) ||
            isPointInsideFrame(frame, p4.x, p4.y)
        ) {
            return true;
        }

        return false;
    }

    isinSelectFrame(frame: AbstractFrame): boolean {
        const p1 = this.getLeftTop();
        const p2 = this.getRightTop();
        const p3 = this.getRightBottom();
        const p4 = this.getLeftBottom();
        
        if (!p1 || !p2 || !p3 || !p4) return false;
        
        if (
            isPointInsideFrame(frame, p1.x, p1.y) &&
            isPointInsideFrame(frame, p2.x, p2.y) &&
            isPointInsideFrame(frame, p3.x, p3.y) &&
            isPointInsideFrame(frame, p4.x, p4.y)
        ) {
            return true;
        }

        return false;
    }

    iscrossSelectFrame(frame: AbstractFrame): boolean {

        var p1 = this.getLeftTop();
        var p2 = this.getRightTop();
        var p3 = this.getRightBottom();
        var p4 = this.getLeftBottom();

        let vec1 = new Vector(p1, p2);
        let vec2 = new Vector(p2, p3);
        let vec3 = new Vector(p3, p4);
        let vec4 = new Vector(p4, p1);

        let vecs = [vec1, vec2, vec3, vec4];

        for (const v of vecs) {
            let xcross1 = findLinesIntersection(v as unknown as Line, frame.vec.getOrtho1X() as unknown as Line);
            let ycross1 = findLinesIntersection(v as unknown as Line, frame.vec.getOrtho1Y() as unknown as Line);
            let xcross2 = findLinesIntersection(v as unknown as Line, frame.vec.getOrtho2X() as unknown as Line);
            let ycross2 = findLinesIntersection(v as unknown as Line, frame.vec.getOrtho2Y() as unknown as Line);

            if (xcross1 || ycross1 || xcross2 || ycross2 || this.isinSelectFrame(frame)) {
                return true;
            }

        }

        return false;

    }

    // --------- MAGNETS ---------
    // for each line:


    getLinesMagnets(mouse: Point): LineMagnet[] {
        var getMagnet = (line: Line, mouse: Point): LineMagnet => {
            var isinGripStart = (mouse: Point) => this.grip.isin(line.start, mouse);
            var isinGripEnd = (mouse: Point) => this.grip.isin(line.end, mouse);
            var isinGripMid = (mouse: Point) => this.grip.isin(line.mid, mouse);
            var isinTripHstart = (mouse: Point) => this.tripH.isin(line.start, mouse);
            var isinTripHmid = (mouse: Point) => this.tripH.isin(line.mid, mouse);
            var isinTripHend = (mouse: Point) => this.tripH.isin(line.end, mouse);
            var isinTripVstart = (mouse: Point) => this.tripV.isin(line.start, mouse);
            var isinTripVmid = (mouse: Point) => this.tripV.isin(line.mid, mouse);
            var isinTripVend = (mouse: Point) => this.tripV.isin(line.end, mouse);

            return {
                line: line,
                gripStart: isinGripStart(mouse),
                gripEnd: isinGripEnd(mouse),
                gripMid: isinGripMid(mouse),
                tripHStart: isinTripHstart(mouse),
                tripHMid: isinTripHmid(mouse),
                tripHEnd: isinTripHend(mouse),
                tripVStart: isinTripVstart(mouse),
                tripVMid: isinTripVmid(mouse),
                tripVEnd: isinTripVend(mouse)
            }
        }

        var lines = this.shapes.filter(s => s.type === ObjectTypes.line) as Line[];
        var magnets: LineMagnet[] = [];

        for (var i = 0; i < lines.length; i++) {

            var line = lines[i];
            var magnet = getMagnet(line, mouse);

            if (magnet) {
                magnets.push(magnet);
            }
        }

        return magnets;

    }

    // for each rectangle:
    getRectanglesMagnets(mouse: Point): RectangleMagnet[] {
        var getMagnets = (rect: Rectangle, mouse: Point): RectangleMagnet => {
            var isinGripP1 = (mouse: Point) => this.grip.isin(rect.p1, mouse);
            var isinGripP2 = (mouse: Point) => this.grip.isin(rect.p2, mouse);
            var isinGripP3 = (mouse: Point) => this.grip.isin(rect.p3, mouse);
            var isinGripP4 = (mouse: Point) => this.grip.isin(rect.p4, mouse);
            var isinGripM1 = (mouse: Point) => this.grip.isin(rect.m1, mouse);
            var isinGripM2 = (mouse: Point) => this.grip.isin(rect.m2, mouse);
            var isinGripM3 = (mouse: Point) => this.grip.isin(rect.m3, mouse);
            var isinGripM4 = (mouse: Point) => this.grip.isin(rect.m4, mouse);
            var isinGripCenter = (mouse: Point) => this.grip.isin(rect.center, mouse);
            var isinTripHtop = (mouse: Point) => this.tripH.isin(rect.m1, mouse);
            var isinTripHbottom = (mouse: Point) => this.tripH.isin(rect.m3, mouse);
            var isinTripVleft = (mouse: Point) => this.tripV.isin(rect.m4, mouse);
            var isinTripVright = (mouse: Point) => this.tripV.isin(rect.m2, mouse);
            var isinTripVMtop = (mouse: Point) => this.tripV.isin(rect.m1, mouse);
            var isinTripVMbottom = (mouse: Point) => this.tripV.isin(rect.m3, mouse);
            var isinTripHMright = (mouse: Point) => this.tripH.isin(rect.m2, mouse);
            var isinTripHMleft = (mouse: Point) => this.tripH.isin(rect.m4, mouse);

            return {
                rect: rect,
                gripP1: isinGripP1(mouse),
                gripP2: isinGripP2(mouse),
                gripP3: isinGripP3(mouse),
                gripP4: isinGripP4(mouse),
                gripM1: isinGripM1(mouse),
                gripM2: isinGripM2(mouse),
                gripM3: isinGripM3(mouse),
                gripM4: isinGripM4(mouse),
                gripCenter: isinGripCenter(mouse),
                tripHtop: isinTripHtop(mouse),
                tripHbottom: isinTripHbottom(mouse),
                tripVleft: isinTripVleft(mouse),
                tripVright: isinTripVright(mouse),
                tripVMtop: isinTripVMtop(mouse),
                tripVMbottom: isinTripVMbottom(mouse),
                tripHMright: isinTripHMright(mouse),
                tripHMleft: isinTripHMleft(mouse)

            }
        }

        var rects = this.shapes.filter(s => s.type === ObjectTypes.rectangle) as Rectangle[];
        var magnets: RectangleMagnet[] = [];

        for (var i = 0; i < rects.length; i++) {

            var rectangle = rects[i] as Rectangle;
            var magnet = getMagnets(rectangle, mouse);

            if (magnet) {
                magnets.push(magnet);
            }
        }

        return magnets;

    }

    // for each circle
    getCirclesMagnets(mouse: Point): CircleMagnet[] {
        var getMagnets = (circle: Circle, mouse: Point): CircleMagnet => {
            var isinGripCenter = (mouse: Point) => this.grip.isin(circle.center, mouse);
            var isinGripQ1 = (mouse: Point) => this.grip.isin(circle.quad1, mouse);
            var isinGripQ2 = (mouse: Point) => this.grip.isin(circle.quad2, mouse);
            var isinGripQ3 = (mouse: Point) => this.grip.isin(circle.quad3, mouse);
            var isinGripQ4 = (mouse: Point) => this.grip.isin(circle.quad4, mouse);
            var isinTripHcenter = (mouse: Point) => this.tripH.isin(circle.center, mouse);
            var isinTripVcenter = (mouse: Point) => this.tripV.isin(circle.center, mouse);
            var isinTripHq1 = (mouse: Point) => this.tripH.isin(circle.quad1, mouse);
            var isinTripHq3 = (mouse: Point) => this.tripH.isin(circle.quad3, mouse);
            var isinTripVq4 = (mouse: Point) => this.tripV.isin(circle.quad4, mouse);
            var isinTripVq2 = (mouse: Point) => this.tripV.isin(circle.quad2, mouse);

            return {
                circle: circle,
                gripCenter: isinGripCenter(mouse),
                gripQ1: isinGripQ1(mouse),
                gripQ2: isinGripQ2(mouse),
                gripQ3: isinGripQ3(mouse),
                gripQ4: isinGripQ4(mouse),
                tripHCenter: isinTripHcenter(mouse),
                tripVCenter: isinTripVcenter(mouse),
                tripHq1: isinTripHq1(mouse),
                tripHq3: isinTripHq3(mouse),
                tripVq4: isinTripVq4(mouse),
                tripVq2: isinTripVq2(mouse)
            }
        }

        var circles = this.shapes.filter(s => s.type === ObjectTypes.circle) as Circle[];
        var magnets: CircleMagnet[] = [];

        for (var i = 0; i < circles.length; i++) {

            var circle = circles[i];
            var magnet = getMagnets(circle, mouse);

            if (magnet) {
                magnets.push(magnet);
            }
        }

        return magnets;

    }

    // --------- MAGNETS ---------

    zoom(zl: number): void {
        this.shapes.forEach(s => {
            s.zoom(zl);
        });

        this.cpoints.forEach(p => {
            p.zoom(zl);
        });

        this.updateSavedVertices();

    }

    pan(tx: number, ty: number): void {
        this.shapes.forEach(s => {
            s.pan(tx, ty);
        });

        this.cpoints.forEach(p => {
            p.pan(tx, ty);
        });

        this.updateSavedVertices();

    }

    getConnectedSegments(): Array<Line | null | undefined> {

        var { cp1, cp2 } = this.getCpoints();


        var segments = getDisplayedShapes().filter(s => s.type === ObjectTypes.line) as Line[];


        var conSegs: Line[] = [];

        for (const seg of segments) {
            if (!seg.start || !seg.end) continue;

            if (isin(seg.start, cp1) || isin(seg.end, cp1)) {
                conSegs.push(seg);
            }
            else if (isin(seg.start, cp2) || isin(seg.end, cp2)) {
                conSegs.push(seg);
            }

        }

        var seg1 = arrayFirst(conSegs);
        var seg2 = arrayLast(conSegs);

        var result = [seg1, seg2];


        return result;
    }

    getConnectedSegmentsPoints(cSegs: Line[], cPoints: ConnectionPoint[]): Array<{ name: string; side: string; cpoint: string; point: Point }> | null {
        var seg1 = arrayFirst(cSegs) as Line | undefined;
        var seg2 = arrayLast(cSegs) as Line | undefined;

        var cp1 = arrayFirst(cPoints) as ConnectionPoint | undefined;
        var cp2 = arrayLast(cPoints) as ConnectionPoint | undefined;

        var result: Array<{ name: string; side: string; cpoint: string; point: Point }> = [];

        if (!seg1 && !seg2) {
            console.error(`!! Блок не соединён с отрезками`);
            return null;
        };

        if (seg1 && !seg2 && cp1) {
            if (seg1.start && isin(seg1.start, cp1.point)) {
                result.push({
                    name: 'seg1',
                    side: 'start',
                    cpoint: 'cp1',
                    point: seg1.start
                })

            }
            if (seg1.end && isin(seg1.end, cp1.point)) {
                result.push({
                    name: 'seg1',
                    side: 'end',
                    cpoint: 'cp1',
                    point: seg1.end
                });
            }

        }
        else if (seg2 && !seg1 && cp2) {
            if (seg2.start && isin(seg2.start, cp2.point)) {
                result.push({
                    name: 'seg2',
                    side: 'start',
                    cpoint: 'cp2',
                    point: seg2.start
                });

            }
            if (seg2.end && isin(seg2.end, cp2.point)) {
                result.push({
                    name: 'seg2',
                    side: 'end',
                    cpoint: 'cp2',
                    point: seg2.end
                });
            }

        }
        else if (seg1 && seg2 && cp1 && cp2) {

            let isinStartSeg1Cp1 = seg1.start ? isin(seg1.start, cp1.point) : false;
            let isinEndSeg1Cp1 = seg1.end ? isin(seg1.end, cp1.point) : false;
            let isinStartSeg1Cp2 = seg1.start ? isin(seg1.start, cp2.point) : false;
            let isinEndSeg1Cp2 = seg1.end ? isin(seg1.end, cp2.point) : false;
            let isinStartSeg2Cp1 = seg2.start ? isin(seg2.start, cp1.point) : false;
            let isinEndSeg2Cp1 = seg2.end ? isin(seg2.end, cp1.point) : false;
            let isinStartSeg2Cp2 = seg2.start ? isin(seg2.start, cp2.point) : false;
            let isinEndSeg2Cp2 = seg2.end ? isin(seg2.end, cp2.point) : false;

            if (isinStartSeg1Cp1 && seg1.start) {
                result.push({
                    name: 'seg1Start',
                    side: 'start',
                    cpoint: 'cp1',
                    point: seg1.start
                });
            }
            if (isinEndSeg1Cp1 && seg1.end) {
                result.push({
                    name: 'seg1End',
                    side: 'end',
                    cpoint: 'cp1',
                    point: seg1.end
                });
            }
            if (isinStartSeg1Cp2 && seg1.start) {
                result.push({
                    name: 'seg1Start',
                    side: 'start',
                    cpoint: 'cp2',
                    point: seg1.start
                });
            }
            if (isinEndSeg1Cp2 && seg1.end) {
                result.push({
                    name: 'seg1End',
                    side: 'end',
                    cpoint: 'cp2',
                    point: seg1.end
                });
            }
            if (isinStartSeg2Cp1 && seg2.start) {
                result.push({
                    name: 'seg2Start',
                    side: 'start',
                    cpoint: 'cp1',
                    point: seg2.start
                });
            }
            if (isinEndSeg2Cp1 && seg2.end) {
                result.push({
                    name: 'seg2End',
                    side: 'end',
                    cpoint: 'cp1',
                    point: seg2.end
                });
            }
            if (isinStartSeg2Cp2 && seg2.start) {
                result.push({
                    name: 'seg2Start',
                    side: 'start',
                    cpoint: 'cp2',
                    point: seg2.start
                });
            }
            if (isinEndSeg2Cp2 && seg2.end) {
                result.push({
                    name: 'seg2End',
                    side: 'end',
                    cpoint: 'cp2',
                    point: seg2.end
                });
            }
        }

        return result;
    }

    getCps() {

        let points = [...new Set(this.cpoints)];

        if (points.length === 0) {
            return {}; // Массив пуст, возвращаем пустой объект
        } else if (points.length === 1) {
            return { cp1: points[0] }; // Есть только одна точка, возвращаем её
        } else {
            return {
                cp1: points[0],
                cp2: points[1]
            }; // Есть минимум две разные точки, возвращаем их
        }

    }

getCpoints() {
    const uniquePoints = [...new Set(this.cpoints.map(p => p.point))]; // Получаем массив уникальных точек

    return Object.fromEntries(uniquePoints.map((point, index) => [`cp${index + 1}`, point]));
}

    static restoreFromDatabase(dbObject: unknown): Block {
        interface DbShape {
            type: string;
            id?: string;
            _start?: { x: number; y: number };
            _end?: { x: number; y: number };
            _p1?: { x: number; y: number };
            _p2?: { x: number; y: number };
            _p3?: { x: number; y: number };
            _p4?: { x: number; y: number };
            _width?: number;
            _height?: number;
            _center?: { x: number; y: number };
            _radius?: number;
            color: number[];
            thickness: number;
            joinedStart?: unknown;
            joinedEnd?: unknown;
            rtype?: string;
            routeId?: string;
        }
        
        interface DbObject {
            shapes: DbShape[];
            cpoints: Array<{ point: { x: number; y: number } }>;
            data: BlockData;
            net: {
                _widthMil?: number;
                _heightMil?: number;
                _diameterMil?: number;
                _flowRateCmh?: number;
                _realLenMet?: number;
                type?: string;
                kms?: number | null;
                kvs?: number | null;
            };
        }

        const db = dbObject as DbObject;
        let freeShapes: (Line | Rectangle | Circle)[] = [];
        db.shapes.forEach((shape: DbShape) => {
            shape.thickness = s.blockThickness;

            switch (shape.type) {
                case ObjectTypes.line:
                    if (!shape._start || !shape._end) break;
                    const start = new Point(shape._start.x, shape._start.y);
                    const end = new Point(shape._end.x, shape._end.y);

                    const line = new Line(start, end, shape.color, shape.thickness);
                    if (shape.id) line.id = shape.id;
                    if (shape.joinedStart) line.joinedStart = shape.joinedStart as unknown as Join;
                    if (shape.joinedEnd) line.joinedEnd = shape.joinedEnd as unknown as Join;
                    if (shape.rtype) line.rtype = shape.rtype;
                    if (shape.routeId) line.routeId = shape.routeId;
                    freeShapes.push(line);
                    break;
                case ObjectTypes.rectangle:
                    if (!shape._p1 || !shape._p2 || !shape._p3 || !shape._p4 || shape._width === undefined || shape._height === undefined) break;
                    const p1 = new Point(shape._p1.x, shape._p1.y);
                    const p2 = new Point(shape._p2.x, shape._p2.y);
                    const p3 = new Point(shape._p3.x, shape._p3.y);
                    const p4 = new Point(shape._p4.x, shape._p4.y);
                    const width = shape._width;
                    const height = shape._height;

                    const rectangle = new Rectangle(p1, p2, p3, p4, width, height, shape.color, shape.thickness);
                    if (shape.rtype) rectangle.rtype = shape.rtype;

                    freeShapes.push(rectangle);
                    break;
                case 'circle':
                    if (!shape._center || shape._radius === undefined) break;
                    let center = new Point(shape._center.x, shape._center.y);
                    const circle = new Circle(center, shape._radius, shape.color, shape.thickness);
                    if (shape.rtype) circle.rtype = shape.rtype;
                    freeShapes.push(circle);
                    break;
            }
        });
        const free = new Block(freeShapes, s.blockColor, s.blockThickness);
        let cpoints: ConnectionPoint[] = [];
        for (const cpoint of db.cpoints) {
            let cp = new ConnectionPoint(cpoint.point.x, cpoint.point.y);
            cpoints.push(cp);
        }
        free.cpoints = cpoints;



        free.data = db.data;
        if (db.net._widthMil && db.net._heightMil) {
            free.net.widthMil = db.net._widthMil;
            free.net.heightMil = db.net._heightMil;
        }
        else if (db.net._diameterMil !== undefined && db.net._diameterMil !== null) {
            free.net.diameterMil = db.net._diameterMil;
        }
        free.net.kms = db.net.kms ?? null;
        free.net.kvs = db.net.kvs ?? null;

        // Apply flow direction properties from database object
        if (db.data && db.data.flowDirections) {
            // Восстановление направлений потока из сохраненных данных
            free.flowDirections = {
                start: Block.convertToFlowDirection(db.data.flowDirections.start),
                end: Block.convertToFlowDirection(db.data.flowDirections.end),
                mid: db.data.flowDirections.mid ? Block.convertToFlowDirection(db.data.flowDirections.mid) : undefined
            };
        }
        
        if (db.data && db.data.isTerminal) {
            // Восстановление признака терминальности из сохраненных данных
            free.isTerminal = {
                start: db.data.isTerminal.start,
                end: db.data.isTerminal.end,
                mid: db.data.isTerminal.mid
            };
        }
        
        if (db.data && db.data.pressure) {
            // Восстановление давления из сохраненных данных
            free.pressure = {
                start: db.data.pressure.start,
                end: db.data.pressure.end,
                mid: db.data.pressure.mid
            };
        }

        return free;

    }

    initNetInfo(): void {
        var size = parseAirDuctSize(this.data.size);
        var kms: number | null = null;
        var kvs: number | null = null;
        
        if (this.data.kms) {
            const kmsValue = typeof this.data.kms === 'string' ? parseFloat(this.data.kms) : Number(this.data.kms);
            kms = isNaN(kmsValue) ? null : kmsValue;
        }
        
        if (this.data.kvs) {
            const kvsValue = typeof this.data.kvs === 'string' ? parseFloat(this.data.kvs) : Number(this.data.kvs);
            kvs = isNaN(kvsValue) ? null : kvsValue;
        }
        
        const sizeValue: number | [number, number] | null = size ?? null;
        this.net = new NetworkInfo(ObjectTypes.block, sizeValue, null, null, null, kms, kvs);
    }

    /**
     * Convert string value to FlowDirection enum
     * @param value - String value ('IN', 'OUT', 'UNKNOWN', etc.) or already a FlowDirection
     */
    private static convertToFlowDirection(value: string | FlowDirection | undefined): FlowDirection {
        if (!value) {
            return FlowDirection.UNKNOWN;
        }
        
        // If already a FlowDirection enum, return as is
        if (typeof value === 'string' && Object.values(FlowDirection).includes(value as FlowDirection)) {
            return value as FlowDirection;
        }
        
        // Convert string to FlowDirection
        const upperValue = (value as string).toUpperCase();
        switch (upperValue) {
            case 'IN':
                return FlowDirection.IN;
            case 'OUT':
                return FlowDirection.OUT;
            case 'UNKNOWN':
                return FlowDirection.UNKNOWN;
            default:
                console.warn(`Unknown flow direction value: ${value}, defaulting to UNKNOWN`);
                return FlowDirection.UNKNOWN;
        }
    }

    getCrucialPoints():RouteCrucialPoints {
        var cPoints = this.getCpoints();
        
        // Update mid properties if third connection point exists
        if (cPoints.cp3) {
            // Обеспечиваем наличие значения направления потока для средней точки
            this.flowDirections.mid = this.flowDirections.mid || FlowDirection.UNKNOWN;
            this.isTerminal.mid = this.isTerminal.mid || false;
            this.pressure.mid = this.pressure.mid || 0;
        } else {
            // Если средней точки нет, сбрасываем параметры
            this.flowDirections.mid = undefined;
            this.isTerminal.mid = undefined;
            this.pressure.mid = undefined;
        }
        
        return {
            start: cPoints.cp1,
            end: cPoints.cp2,
            mid: cPoints.cp3, // Third connection point if it exists
            flowDirections: this.flowDirections,
            isTerminal: this.isTerminal,
            pressure: this.pressure
        }
    }

    getCrucialPointsArray(): Point[] {
        const { start, mid, end } = this.getCrucialPoints();
        const points: Point[] = [];

        if (start) {
            points.push(start);
        }

        if (end) {
            points.push(end);
        }

        if (mid) {
            points.push(mid);
        }

        return points;
    }

    /**
     * Updates flow direction properties for this block
     * @param flowDirections - Flow directions for each crucial point
     * @param isTerminal - Terminal status for each crucial point
     * @param pressure - Pressure levels for each crucial point
     */
    updateFlowProperties(
        flowDirections?: { start?: FlowDirection, end?: FlowDirection, mid?: FlowDirection },
        isTerminal?: { start?: boolean, end?: boolean, mid?: boolean },
        pressure?: { start?: number, end?: number, mid?: number }
    ): void {
        if (flowDirections) {
            if (flowDirections.start !== undefined) this.flowDirections.start = flowDirections.start;
            if (flowDirections.end !== undefined) this.flowDirections.end = flowDirections.end;
            if (flowDirections.mid !== undefined) this.flowDirections.mid = flowDirections.mid;
        }
        
        if (isTerminal) {
            if (isTerminal.start !== undefined) this.isTerminal.start = isTerminal.start;
            if (isTerminal.end !== undefined) this.isTerminal.end = isTerminal.end;
            if (isTerminal.mid !== undefined) this.isTerminal.mid = isTerminal.mid;
        }
        
        if (pressure) {
            if (pressure.start !== undefined) this.pressure.start = pressure.start;
            if (pressure.end !== undefined) this.pressure.end = pressure.end;
            if (pressure.mid !== undefined) this.pressure.mid = pressure.mid;
        }
    }

    checkIsTerminal():boolean {
        // Check if any connection point is marked as terminal
        // mid can be undefined (for blocks with only 2 connection points)
        return this.isTerminal.start === true || 
               this.isTerminal.end === true || 
               (this.isTerminal.mid !== undefined && this.isTerminal.mid === true);
    }

    getTerminalFlowDirection(): FlowDirection | null {
        // terminal flow direction is usually only one
        // Check all connection points for IN or OUT directions
        for (const key of ['start', 'end', 'mid']) {
            const direction = this.flowDirections[key as keyof typeof this.flowDirections];
            if (direction === FlowDirection.IN || direction === FlowDirection.OUT) {
                return direction;
            }
        }
        return null;
    }


}

function getShapesPoints(shapes: (Line | Rectangle | Circle)[]): { xes: number[]; yes: number[] } {
    var lineXes = shapes.filter((s): s is Line => s.type === ObjectTypes.line).map((s: Line) => [s.start?.x ?? 0, s.end?.x ?? 0]).flat();
    var rectXes = shapes.filter((s): s is Rectangle => s.type === ObjectTypes.rectangle).map((s: Rectangle) => [s.p1?.x ?? 0, s.p2?.x ?? 0, s.p3?.x ?? 0, s.p4?.x ?? 0]).flat();
    var lineYes = shapes.filter((s): s is Line => s.type === ObjectTypes.line).map((s: Line) => [s.start?.y ?? 0, s.end?.y ?? 0]).flat();
    var rectYes = shapes.filter((s): s is Rectangle => s.type === ObjectTypes.rectangle).map((s: Rectangle) => [s.p1?.y ?? 0, s.p2?.y ?? 0, s.p3?.y ?? 0, s.p4?.y ?? 0]).flat();

    var circleXes = shapes.filter((s): s is Circle => s.type === ObjectTypes.circle).map((s: Circle) => [s.center?.x ?? 0, s.quad1?.x ?? 0, s.quad2?.x ?? 0, s.quad3?.x ?? 0, s.quad4?.x ?? 0]).flat();
    var circleYes = shapes.filter((s): s is Circle => s.type === ObjectTypes.circle).map((s: Circle) => [s.center?.y ?? 0, s.quad1?.y ?? 0, s.quad2?.y ?? 0, s.quad3?.y ?? 0, s.quad4?.y ?? 0]).flat();



    var xes = lineXes.concat(rectXes).concat(circleXes);
    var yes = lineYes.concat(rectYes).concat(circleYes);
    return { xes, yes };
}

function concat(arrays: Float32Array[]): Float32Array | null {
    // sum of individual array lengths
    let totalLength = arrays.reduce((acc: number, value: Float32Array) => acc + value.length, 0);

    if (!arrays.length) return null;

    let result = new Float32Array(totalLength);

    // for each array - copy it over result
    // next array is copied right after the previous one
    let length = 0;
    for (let array of arrays) {
        result.set(array, length);
        length += array.length;
    }

    return result;
}
